import { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiTrashCanOutline, mdiCheckCircle, mdiClose } from "@mdi/js";
import { useHistory } from "react-router-dom";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import {
  AddressModel,
  CreateReplacementSTPayload,
  ItemRow,
  ItemWarehouseModel,
} from "../../model/warehouse_outward_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import {
  CreateReplacementStockTransfer,
  GetAvailableQty,
  GetCustomerBillingAddresses,
  GetCustomerShippingAddresses,
  GetDeviceDetailsFromComplaint,
  GetHomeAddressByDeviceId,
  GetItemWAC,
} from "../../warehouse_module_repo";
import { formatAddress } from "../../warehouse_helper";
import LoadingWidget from "../../../../../context_providers/modal/loader";
import RequiredMark from "../../../../new_components/common/required_mark";
import UserSearchFieldV2 from "../../../orders/order_create/customer_section/customer/user_search_widgetV2";
import { UserModel } from "../../../../../context_providers/auth/user_model";
import { stateData } from "../../../../../utils/states_data";
import { CountryType } from "../../../../../erp/accounts/models/party_model";
import { Country } from "country-state-city";
import ItemSearchFieldV2 from "../../../../../erp/inventory/items/item_search_fieldv2";
import { validateWarehouseAddress } from "../../../../../utils/warehouse_util";
import { getMissingAddressFields } from "../../../../../utils/location_util";

export default function ReplacementSTFormFG(): JSX.Element {
  const history = useHistory();
  const { showToast } = useToast();

  const [fromWarehouse, setFromWarehouse] = useState<WarehouseModel | null>(
    null,
  );

  // customer
  const [billingAddress, setBillingAddress] = useState<AddressModel | null>(
    null,
  );
  const [shippingAddress, setShippingAddress] = useState<AddressModel | null>(
    null,
  );
  const [manualSelectedCountry, setManualSelectedCountry] = useState("");
  const [expectedDate, setExpectedDate] = useState<string>("");
  const [complaintId, setComplaintId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [items, setItems] = useState<ItemRow[]>([]);
  const [error, setError] = useState<string>("");
  const [purpose, setPurpose] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [contactName, setContactName] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [logisticPartner, setLogisticPartner] = useState<string>("");
  const [trackingId, setTrackingId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  // Blocks a second submit before the disabled button has re-rendered.
  const submittingRef = useRef(false);
  const [mode, setMode] = useState<"fetch" | "form">("fetch");
  const [fetchedDevices, setFetchedDevices] = useState<any[]>([]);
  const [faultyDevices, setFaultyDevices] = useState<any[]>();
  const [orderId, setOrderId] = useState<string>("");
  const [shipTo, setShipTo] = useState<"dealership" | "end_user">("dealership");
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [homeAddress, setHomeAddress] = useState<any>(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [manualAddress, setManualAddress] = useState<Partial<AddressModel>>({
    line1: "",
    city: "",
    state: "",
    country: "",
    pincode: 0,
  });
  const [availableQtyMap, setAvailableQtyMap] = useState<
    Record<string, number>
  >({});
  const [customer, setCustomer] = useState<UserModel>();
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [billingOptions, setBillingOptions] = useState<AddressModel[]>([]);
  const [shippingOptions, setShippingOptions] = useState<AddressModel[]>([]);
  const [addressMode, setAddressMode] = useState<"auto" | "manual">("auto");
  const [manualItemMode, setManualItemMode] = useState(false);
  const [countries, setCountries] = useState<CountryType[]>([]);
  useEffect(() => {
    if (manualAddress.country) {
      setManualSelectedCountry(manualAddress.country);
    }
  }, [manualAddress.country]);
  // Load countries on component mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);
  // useEffect(() => {
  //   if (shipTo === "end_user" && !loadingAddress && !homeAddress) {
  //     setAddressMode("manual");
  //   }
  // }, [homeAddress, loadingAddress, shipTo]);
  useEffect(() => {
    const fetchAllQty = async () => {
      if (!fromWarehouse?.id) return;

      try {
        setLoading(true);

        const res = await GetAvailableQty(fromWarehouse.id);

        if (!res?.success || !res?.data?.item_data) {
          setAvailableQtyMap({});
          return;
        }

        const map: Record<string, number> = {};

        res.data.item_data.forEach((item: any) => {
          map[item.item_code] = item.available_units ?? 0;
        });

        setAvailableQtyMap(map);
      } catch (err) {
        console.error(err);
        setAvailableQtyMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchAllQty();
  }, [fromWarehouse?.id]); // ?? ONLY when warehouse changes

  const fetchAvailableQty = (itemId: string): number => {
    return availableQtyMap[itemId] ?? 0;
  };

  const handleWarehouseSelect = (warehouse: WarehouseModel | null) => {
    if (!warehouse) {
      setFromWarehouse(null);
      return;
    }

    try {
      validateWarehouseAddress(warehouse.address!);
      setFromWarehouse(warehouse);
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message,
      });
      setFromWarehouse(null);
    }
  };

  // ---------- fetch home address ----------

  const fetchHomeAddress = async (deviceId: string) => {
    if (!deviceId) return;

    try {
      setLoadingAddress(true);
      setAddressError("");

      const response = await GetHomeAddressByDeviceId(deviceId);
      const data = await response;

      if (data.success && data.data) {
        setHomeAddress(data.data);
      } else {
        setAddressError(
          data.message || "No home address found for this device",
        );
        setHomeAddress(null);
      }
    } catch (error: any) {
      setAddressError(error.message || "Failed to fetch home address");
      setHomeAddress(null);
    } finally {
      setLoadingAddress(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!customer?.uid) return;

      const [billingRes, shippingRes] = await Promise.all([
        GetCustomerBillingAddresses(customer.uid),
        GetCustomerShippingAddresses(customer.uid),
      ]);

      if (billingRes?.success)
        setBillingOptions(
          (billingRes.data?.billing_profiles || []).map((p: any) => p.address),
        );

      if (shippingRes?.success)
        setShippingOptions(
          (shippingRes.data?.billing_profiles || []).map((p: any) => p.address),
        );
    };

    load();
  }, [customer]);

  useEffect(() => {
    const updateAvailableQty = async () => {
      if (!fromWarehouse || items.length === 0) return;

      const res = await GetAvailableQty(fromWarehouse.id);
      if (!res?.success || !res?.data?.item_data) return;

      const stockMap = new Map<string, number>(
        res.data.item_data.map((row: any) => [
          row.item_code,
          row.available_units ?? 0,
        ]),
      );

      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          availableQty: stockMap.get(item.itemCode) ?? 0,
        })),
      );
    };

    updateAvailableQty();
  }, [fromWarehouse?.id, items.length]);

  // Fetch home address when shipTo changes to end_user
  useEffect(() => {
    if (shipTo === "end_user") {
      let deviceId: string | undefined;

      // Try to get device ID from fetchedDevices first
      if (fetchedDevices.length > 0) {
        deviceId = fetchedDevices[0]?.device_id;
      }
      // If fetchedDevices is empty, try faultyDevices (which are device IDs as strings)
      else if (faultyDevices && faultyDevices.length > 0) {
        deviceId = faultyDevices[0];
      }

      if (deviceId) {
        fetchHomeAddress(deviceId);
      }
    }
  }, [shipTo, fetchedDevices, faultyDevices]);

  // ---------- helpers ----------

  const handleFetchComplaint = async () => {
    if (!complaintId) {
      showToast({ type: ToastType.warning, text: "Enter Complaint ID" });
      return;
    }
    setCustomer(undefined);
    setFetchedDevices([]);
    setItems([]);
    setCustomerName("");
    setCustomerId("");
    setBillingAddress(null);
    setShippingAddress(null);
    setBillingOptions([]);
    setShippingOptions([]);
    setShowCustomerSelect(false);
    try {
      const res = await GetDeviceDetailsFromComplaint(complaintId.trim());
      const json = await res;

      if (!json?.success) {
        showToast({ type: ToastType.error, text: json?.error });
        return;
      }

      const {
        devices,
        locked_devices,
        orderDevices,
        shipping_address,
        orderId,
        customer_name,
        customer_id,
        billing_address,
      } = json.data;

      setFaultyDevices(devices);

      if (locked_devices?.length) {
        showToast({
          type: ToastType.warning,
          text: `Already on an active Replacement ST, left out: ${locked_devices
            .map((l: any) => `${l.device_id} (${l.st_id})`)
            .join(", ")}`,
        });
      }

      if (!orderDevices?.length) {
        // ? MANUAL MODE
        setManualItemMode(true);
        setFetchedDevices([]); // no enriched devices
        setItems([]); // user must pick item

        setShowCustomerSelect(true);
        setMode("form");
        setShippingAddress(null);
        setBillingAddress(null);
        setOrderId("");

        showToast({
          type: ToastType.warning,
          text: "Order details missing. Please select item manually.",
        });

        return;
      }

      const hasValidItemCodes = orderDevices.every(
        (d: any) => d.item_code && String(d.item_code).trim() !== "",
      );

      setOrderId(orderId);
      setCustomerName(customer_name || "");
      setCustomerId(customer_id || "");
      setShippingAddress(shipping_address);
      setBillingAddress(billing_address);
      if (!shippingAddress) {
        setShowCustomerSelect(true);
      }
      setMode("form");

      if (!hasValidItemCodes) {
        // Order info usable, item_codes missing → user must pick item manually
        setManualItemMode(true);
        setFetchedDevices([]);
        setItems([]);
        showToast({
          type: ToastType.warning,
          text: "Item code missing in order. Please select item manually.",
        });
        return;
      }

      // ? AUTO MODE
      setManualItemMode(false);
      setFetchedDevices(orderDevices);

      //  4. Group devices ? item rows
      const grouped: Record<string, any> = {};

      for (const d of orderDevices) {
        if (!grouped[d.item_code]) {
          grouped[d.item_code] = {
            itemCode: d.item_code,
            itemName: d.item_name,
            quantity: 0,
            itemId: d.item_id,
            itemHsn: d.itemHsn,
          };
        }

        grouped[d.item_code].quantity += 1;
      }

      const itemRows: ItemRow[] = [];

      for (const key of Object.keys(grouped)) {
        const row = grouped[key];
        console.log("item_code :", row);

        const wac = await fetchItemWac(row.itemCode);

        itemRows.push({
          itemCode: row.itemCode,
          itemName: row.itemName,
          quantity: row.quantity,
          unitPrice: wac.value,
          isWacFetched: wac.isFetched,
          availableQty: 0, // will be checked after warehouse select
          itemUnit: "",
          itemHsn: row.itemHsn,
          subcategory: "",
        });
      }

      setItems(itemRows);
      if (itemRows.some((item) => !item.isWacFetched)) {
        showToast({
          type: ToastType.warning,
          text: "Some items have WAC 0. Enter unit price manually before creating.",
        });
      }
      //  Switch to full form
      setMode("form");

      showToast({
        type: ToastType.success,
        text: "Devices matched successfully",
      });
    } catch {
      showToast({ type: ToastType.error, text: "Fetch failed" });
    }
  };

  const fetchItemWac = async (
    itemCode: string,
  ): Promise<{ value: number; isFetched: boolean }> => {
    try {
      const res = await GetItemWAC(itemCode);

      if (res?.success && typeof res?.data?.wac_value === "number") {
        const value = res.data.wac_value;
        return { value, isFetched: value > 0 };
      }
      return { value: 0, isFetched: false };
    } catch {
      return { value: 0, isFetched: false };
    }
  };

  const removeItem = (i: number) =>
    setItems((p) => p.filter((_, x) => x !== i));

  const totalValue = items.reduce((t, i) => t + i.quantity * i.unitPrice, 0);
  const deviceMap = manualItemMode
    ? // manual mode: all devices belong to selected item
      items.reduce((acc: any, item) => {
        acc[item.itemCode] = faultyDevices || [];
        return acc;
      }, {})
    : // auto mode: group enriched devices
      fetchedDevices.reduce((acc: any, device: any) => {
        if (!acc[device.item_code]) acc[device.item_code] = [];
        acc[device.item_code].push(device.device_id);
        return acc;
      }, {});

  const handleRemoveDevice = (index: number) => {
    const activeDevices = manualItemMode ? faultyDevices : fetchedDevices;

    if (!activeDevices || activeDevices.length <= 1) {
      showToast({
        type: ToastType.warning,
        text: "At least one faulty device is required",
      });
      return;
    }

    if (manualItemMode) {
      const updated = faultyDevices!.filter((_, i) => i !== index);
      setFaultyDevices(updated);

      // auto reduce quantity
      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          quantity: updated.length,
        })),
      );
    } else {
      const updatedDevices = fetchedDevices.filter((_, i) => i !== index);
      setFetchedDevices(updatedDevices);
      setFaultyDevices(updatedDevices.map((d) => d.device_id));

      const grouped: Record<string, any> = {};
      for (const d of updatedDevices) {
        if (!grouped[d.item_code]) grouped[d.item_code] = { quantity: 0 };
        grouped[d.item_code].quantity += 1;
      }

      setItems((prev) =>
        prev
          .map((item) => ({
            ...item,
            quantity: grouped[item.itemCode]?.quantity || 0,
          }))
          .filter((i) => i.quantity > 0),
      );
    }
  };
  // ---------- submit ----------

  const handleCreate = async () => {
    if (!fromWarehouse) return setError("Select warehouse");
    if (!expectedDate) return setError("Select expected date");
    if (!purpose.trim()) return setError("Enter purpose");
    if (!description.trim()) return setError("Enter description");
    if (!contactName.trim()) return setError("Please enter contact name");

    if (!String(contactNumber).trim())
      return setError("Please enter contact number");

    if (String(contactNumber).replace(/\D/g, "").length < 10)
      return setError("Contact number must be at least 10 digits");
    if (items.length === 0) return setError("Add at least one item");
    if (items.some((i) => i.quantity <= 0))
      return setError("All items must have quantity greater than 0");
    if (items.some((i) => i.unitPrice <= 0))
      return setError("Item can't be added with wac value 0");

    // Check if quantity exceeds available faulty devices
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
    if (totalQty > (faultyDevices?.length || 0)) {
      return setError(
        `Total quantity cannot exceed ${faultyDevices?.length || 0} faulty device(s)`,
      );
    }

    // FG replacement: when an item came from the fetched order (deviceMap has
    // entries for it), enforce that requestedQty matches its device count.
    // User-added replacement SKUs (not in the original order, e.g. when the
    // order's item_code is stale) skip this per-item match — they only have to
    // satisfy the overall total-quantity ceiling checked above.
    for (const i of items) {
      const ids = deviceMap[i.itemCode] || [];
      if (ids.length > 0 && i.quantity !== ids.length) {
        return setError(
          `${i.itemName} (${i.itemCode}): requested qty ${i.quantity} does not match ${ids.length} faulty device ID(s)`,
        );
      }
    }

    if (showCustomerSelect && !customerId) {
      return setError("Select customer");
    }
    if (shipTo === "end_user") {
      if (addressMode === "auto" && !homeAddress) {
        return setError("Home address not available");
      }

      if (
        addressMode === "manual" &&
        (!manualAddress.line1 ||
          !manualAddress.city ||
          !manualAddress.state ||
          !manualAddress.country ||
          !manualAddress.pincode)
      ) {
        return setError("Fill all manual address fields");
      }
    }

    if (showCustomerSelect && shipTo === "dealership" && !shippingAddress) {
      return setError("Select shipping address");
    }

    if (showCustomerSelect && shipTo === "dealership") {
      const billingMissing = getMissingAddressFields(billingAddress, {
        relaxForNonIndia: true,
      });
      if (billingMissing.length)
        return setError(
          `Billing address is missing ${billingMissing.join(", ")}. Please edit the customer address and try again.`,
        );
      const shippingMissing = getMissingAddressFields(shippingAddress, {
        relaxForNonIndia: true,
      });
      if (shippingMissing.length)
        return setError(
          `Shipping address is missing ${shippingMissing.join(", ")}. Please edit the customer address and try again.`,
        );
    }
    // const finalShippingAddress =
    //   shipTo === "end_user"
    //     ? homeAddress ||
    //       (manualAddress.line1 &&
    //       manualAddress.city &&
    //       manualAddress.state &&
    //       manualAddress.country &&
    //       manualAddress.pincode
    //         ? (manualAddress as AddressModel)
    //         : undefined)
    // : (shippingAddress ?? undefined);
    // Check if all items have HSN codes
    if (items.some((i) => !i.itemHsn || i.itemHsn.trim() === ""))
      return setError("All items must have HSN codes before creating");

    const finalShippingAddress =
      shipTo === "end_user"
        ? addressMode === "auto"
          ? homeAddress
          : (manualAddress as AddressModel)
        : (shippingAddress ?? undefined);
    if (!finalShippingAddress) {
      return setError("Shipping address is required");
    }

    const payload: CreateReplacementSTPayload = {
      from_warehouse_id: fromWarehouse.id,
      from_warehouse_name: fromWarehouse.name,
      complaint_id: complaintId.trim(),
      service_id: serviceId || undefined,
      customer_name: customerName,
      customer_id: customerId,
      billing_address: billingAddress ?? undefined,
      shipping_address: finalShippingAddress,
      expected_date: new Date(expectedDate),
      linked_order_id: orderId,
      contact_name: contactName,
      contact_number: contactNumber,
      //  SAME DATA AS items + deviceIds
      //  deviceIds always carries the full faultyDevices list from the
      //  complaint — independent of the user's replacement SKU choice, so the
      //  backend gets the right faulty device IDs even when the user replaces
      //  the auto-populated item row with a different SKU.
      faulty_items: items.map((i) => ({
        inventoryItemCode: i.itemCode,
        itemName: i.itemName,
        requestedQty: i.quantity, // from items
        itemPrice: i.unitPrice, // from items
        subCategory: i.subcategory,
        itemHSN: i.itemHsn,
        itemUnit: i.itemUnit,
        tax_detail: {
          igst: 0,
          cgst: 0,
          sgst: 0,
        },
        gst_applicable: false,
        deviceIds: faultyDevices || [],
      })),

      // original items
      items: items.map(
        (i): ItemWarehouseModel => ({
          inventoryItemCode: i.itemCode,
          itemName: i.itemName,
          requestedQty: i.quantity,
          itemPrice: i.unitPrice,
          subCategory: i.subcategory,
          itemHSN: i.itemHsn,
          itemUnit: i.itemUnit,
          tax_detail: {
            igst: 0,
            cgst: 0,
            sgst: 0,
          },
          gst_applicable: false,
        }),
      ),

      description,
      purpose,
      shipping_details: {
        logistic_partner: logisticPartner.trim() || undefined,
        tracking_id: trackingId.trim() || undefined,
      },

      item_type: "FG",
    };

    console.log("final payload:", payload);

    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      setLoading(true);
      const res = await CreateReplacementStockTransfer(payload);

      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "ST Creation Failed",
        });
        return;
      }

      showToast({ type: ToastType.success, text: "ST Created Successfully" });
      history.goBack();
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  // ---------- UI ----------

  return (
    <div id="warehouse-stock-transfer-replacement-fg-form">
      {/* MODE 1 � INITIAL FETCH VIEW */}
      {mode === "fetch" && (
        <div>
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Complaint ID <RequiredMark />
              </label>
              <div className="flex gap-2">
                <input
                  id="warehouse-stock-transfer-replacement-fg-form-fetch-complaint-id"
                  value={complaintId}
                  onChange={(e) => setComplaintId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input px-3"
                  placeholder="e.g., COMP-2024-0045"
                />
                <button
                  id="warehouse-stock-transfer-replacement-fg-form-fetch-btn"
                  onClick={handleFetchComplaint}
                  className="h-10 px-4 border rounded-md text-sm"
                >
                  Fetch
                </button>
              </div>
            </div>
          </div>

          <div className="border-t px-6 py-4 flex justify-end gap-3">
            <button
              id="warehouse-stock-transfer-replacement-fg-form-fetch-cancel-btn"
              onClick={() => history.goBack()}
              className="border px-6 py-2 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* MODE 2 � FULL FORM */}
      {mode === "form" && (
        <div>
          <div className="p-6 space-y-6">
            {/* Complaint ID and Service ID */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Complaint ID <RequiredMark />
                </label>
                <div className="flex gap-2">
                  <input
                    id="warehouse-stock-transfer-replacement-fg-form-complaint-id"
                    value={complaintId}
                    onChange={(e) => setComplaintId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input px-3"
                  />
                  <button
                    id="warehouse-stock-transfer-replacement-fg-form-refetch-btn"
                    onClick={handleFetchComplaint}
                    className="h-10 px-4 border rounded-md text-sm"
                  >
                    Fetch
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Service ID</label>
                <input
                  id="warehouse-stock-transfer-replacement-fg-form-service-id"
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input px-3"
                  placeholder="e.g., SRV-2024-0123"
                />
              </div>
            </div>

            {/* Faulty Devices Table */}
            <div className="rounded-lg border">
              <div className="p-3 text-sm font-medium bg-muted">
                Faulty Devices ({faultyDevices?.length})
              </div>

              {fetchedDevices.map((d, i) => (
                <div
                  key={i}
                  id={`warehouse-stock-transfer-replacement-fg-form-device-row-${d.device_id ?? i}`}
                  className="p-3 border-t text-sm grid grid-cols-5 items-center"
                >
                  <span className="font-mono">{d.device_id}</span>
                  <span>{d.order_id}</span>
                  <span>{d.item_code}</span>
                  <span>{d.item_name}</span>

                  <div className="text-right">
                    {fetchedDevices.length > 1 && (
                      <button
                        id={`warehouse-stock-transfer-replacement-fg-form-device-row-${d.device_id ?? i}-remove-btn`}
                        onClick={() => handleRemoveDevice(i)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Icon path={mdiTrashCanOutline} size={0.8} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {fetchedDevices?.length === 0 &&
                faultyDevices?.map((d, i) => (
                  <div
                    key={i}
                    id={`warehouse-stock-transfer-replacement-fg-form-faulty-device-row-${d ?? i}`}
                    className="p-3 border-t text-sm grid grid-cols-5 items-center"
                  >
                    <span className="font-mono">{d}</span>

                    <div className="text-right">
                      {faultyDevices?.length > 1 && (
                        <button
                          id={`warehouse-stock-transfer-replacement-fg-form-faulty-device-row-${d ?? i}-remove-btn`}
                          onClick={() => handleRemoveDevice(i)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon path={mdiTrashCanOutline} size={0.8} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            {/* Matched Card */}
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
              <div className="flex items-start gap-3">
                {fetchedDevices.length > 0 && (
                  <Icon
                    path={mdiCheckCircle}
                    size={1}
                    className="text-primary mt-0.5"
                  />
                )}
                {fetchedDevices.length === 0 &&
                  faultyDevices &&
                  faultyDevices?.length > 0 && (
                    <Icon
                      path={mdiClose}
                      size={1}
                      className="text-primary mt-0.5"
                    />
                  )}
                <div className="space-y-1">
                  <p className="text-sm font-medium">Matched: {orderId}</p>
                  {customerName && (
                    <p className="text-sm text-muted-foreground">
                      Bill To:{" "}
                      <span className="font-medium text-foreground">
                        {customerName}
                      </span>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {fetchedDevices.length} device(s) matched
                  </p>
                </div>
              </div>
            </div>

            {/* Ship To */}
            <div className="space-y-3">
              <label className="text-sm font-medium leading-none">
                No items added Ship To *
              </label>

              <div className="space-y-3">
                {showCustomerSelect && (
                  <>
                    <div className="w-1/2 gap-4">
                      <label className="text-sm font-medium">
                        Customer <RequiredMark />
                      </label>

                      <UserSearchFieldV2
                        id="warehouse-stock-transfer-replacement-fg-form-customer"
                        value={customer as any}
                        onSelect={(c: any) => {
                          setCustomer(c);

                          // ?? IMPORTANT: sync with main form
                          setCustomerName(c?.first_name || "");
                          setCustomerId(c?.uid || "");

                          // reset addresses when customer changes
                          setBillingAddress(null);
                          setShippingAddress(null);
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <select
                        id="warehouse-stock-transfer-replacement-fg-form-billing-address"
                        className="h-10 border rounded-md px-3"
                        value={billingAddress?.id || ""}
                        onChange={(e) => {
                          const selected = billingOptions.find(
                            (a) => a.id === e.target.value,
                          );
                          setBillingAddress(selected || null);
                        }}
                      >
                        <option value="">Billing Address</option>

                        {billingOptions.map((a) => (
                          <option key={a.id} value={a.id}>
                            {formatAddress(a)}
                          </option>
                        ))}
                      </select>

                      <select
                        id="warehouse-stock-transfer-replacement-fg-form-shipping-address"
                        className="h-10 border rounded-md px-3"
                        value={shippingAddress?.id || ""}
                        onChange={(e) => {
                          const selected = shippingOptions.find(
                            (a) => a.id === e.target.value,
                          );
                          setShippingAddress(selected || null);
                        }}
                      >
                        <option value="">Shipping Address</option>

                        {shippingOptions.map((a) => (
                          <option key={a.id} value={a.id}>
                            {formatAddress(a)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <label
                  className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/30 cursor-pointer"
                  onClick={() => setShipTo("dealership")}
                >
                  <input
                    id="warehouse-stock-transfer-replacement-fg-form-ship-to-dealership"
                    type="radio"
                    name="shipto"
                    value="dealership"
                    checked={shipTo === "dealership"}
                    onChange={() => setShipTo("dealership")}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm">At Dealership</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {shippingAddress
                        ? formatAddress(shippingAddress)
                        : "No address"}
                    </p>
                  </div>
                </label>
                <label
                  className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/30 cursor-pointer"
                  onClick={() => setShipTo("end_user")}
                >
                  <input
                    id="warehouse-stock-transfer-replacement-fg-form-ship-to-end-user"
                    type="radio"
                    name="shipto"
                    value="end_user"
                    checked={shipTo === "end_user"}
                    onChange={() => setShipTo("end_user")}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm">At End User</p>
                  </div>
                </label>
              </div>

              {shipTo === "end_user" && (
                <div className="space-y-3">
                  {/* Toggle */}
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        id="warehouse-stock-transfer-replacement-fg-form-address-mode-auto"
                        type="radio"
                        checked={addressMode === "auto"}
                        onChange={() => setAddressMode("auto")}
                      />
                      <span className="text-sm">Use Home Address</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        id="warehouse-stock-transfer-replacement-fg-form-address-mode-manual"
                        type="radio"
                        checked={addressMode === "manual"}
                        onChange={() => setAddressMode("manual")}
                      />
                      <span className="text-sm">Enter Manually</span>
                    </label>
                  </div>

                  {/* AUTO MODE */}
                  {addressMode === "auto" && (
                    <>
                      {loadingAddress ? (
                        <p className="text-xs text-muted-foreground">
                          Loading address...
                        </p>
                      ) : homeAddress ? (
                        <div className="p-3 rounded-lg bg-muted/50 border">
                          <p className="text-xs text-muted-foreground mb-1">
                            Home Address
                          </p>
                          <p className="text-sm font-medium">
                            {homeAddress.address}, {homeAddress.city},{" "}
                            {homeAddress.state} {homeAddress.pincode}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-red-600">
                          {addressError || "Home address not available"}
                        </p>
                      )}
                    </>
                  )}

                  {/* MANUAL MODE */}
                  {addressMode === "manual" && (
                    <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 space-y-3">
                      <p className="text-xs font-medium">
                        Enter Address Manually
                      </p>

                      <input
                        id="warehouse-stock-transfer-replacement-fg-form-manual-address-line1"
                        type="text"
                        placeholder="Address Line 1*"
                        value={manualAddress.line1 || ""}
                        onChange={(e) =>
                          setManualAddress((prev) => ({
                            ...prev,
                            line1: e.target.value,
                          }))
                        }
                        className="w-full h-10 border rounded-md px-3 text-sm"
                      />

                      <div className="grid grid-cols-2 gap-2">
                        <input
                          id="warehouse-stock-transfer-replacement-fg-form-manual-address-city"
                          type="text"
                          placeholder="City*"
                          value={manualAddress.city || ""}
                          onChange={(e) =>
                            setManualAddress((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                          className="w-full h-10 border rounded-md px-3 text-sm"
                        />

                        {manualSelectedCountry === "India" ? (
                          <select
                            id="warehouse-stock-transfer-replacement-fg-form-manual-address-state-select"
                            className="w-full h-10 border rounded-md px-3 text-sm"
                            value={manualAddress.state || ""}
                            onChange={(e) =>
                              setManualAddress((prev) => ({
                                ...prev,
                                state: e.target.value,
                              }))
                            }
                          >
                            <option value="">Select State</option>
                            {stateData.map((state) => (
                              <option key={state.id} value={state.name}>
                                {state.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id="warehouse-stock-transfer-replacement-fg-form-manual-address-state-input"
                            type="text"
                            placeholder="State*"
                            value={manualAddress.state || ""}
                            onChange={(e) =>
                              setManualAddress((prev) => ({
                                ...prev,
                                state: e.target.value,
                              }))
                            }
                            className="w-full h-10 border rounded-md px-3 text-sm"
                          />
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <select
                          id="warehouse-stock-transfer-replacement-fg-form-manual-address-country"
                          className="w-full h-10 border rounded-md px-3 text-sm"
                          value={manualAddress.country || ""}
                          onChange={(e) => {
                            const value = e.target.value;

                            setManualSelectedCountry(value);

                            setManualAddress((prev) => ({
                              ...prev,
                              country: value,
                              state: "", // reset state when country changes
                            }));
                          }}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.isoCode} value={country.name}>
                              {country.name}
                            </option>
                          ))}
                        </select>

                        <input
                          id="warehouse-stock-transfer-replacement-fg-form-manual-address-pincode"
                          type="number"
                          placeholder="Pincode*"
                          value={manualAddress.pincode || ""}
                          onChange={(e) =>
                            setManualAddress((prev) => ({
                              ...prev,
                              pincode: Number(e.target.value),
                            }))
                          }
                          className="w-full h-10 border rounded-md px-3 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {shipTo === "dealership" && shippingAddress && (
                <div className="p-3 rounded-lg bg-muted/50 border">
                  <p className="text-xs text-muted-foreground mb-1">
                    Shipping Address (from SO)
                  </p>
                  <p className="text-sm font-medium">
                    {formatAddress(shippingAddress)}
                  </p>
                </div>
              )}
            </div>

            {/* Warehouse + Expected Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">
                  Warehouse <RequiredMark />
                </label>
                <WarehouseSearchField
                  id="warehouse-stock-transfer-replacement-fg-form-warehouse"
                  value={fromWarehouse || undefined}
                  onSelect={handleWarehouseSelect}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  Expected Date <RequiredMark />
                </label>
                <input
                  id="warehouse-stock-transfer-replacement-fg-form-expected-date"
                  type="date"
                  value={expectedDate}
                  onChange={(e) => setExpectedDate(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">
                  Contact Name <RequiredMark />
                </label>
                <input
                  id="warehouse-stock-transfer-replacement-fg-form-contact-name"
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
              <div>
                <label className="text-sm font-medium">
                  Contact Number <RequiredMark />
                </label>
                <input
                  id="warehouse-stock-transfer-replacement-fg-form-contact-number"
                  type="text"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
            </div>

            {/* Purpose */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">
                  Purpose <RequiredMark />
                </label>
                <input
                  id="warehouse-stock-transfer-replacement-fg-form-purpose"
                  type="text"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium">
                Description <RequiredMark />
              </label>
              <textarea
                id="warehouse-stock-transfer-replacement-fg-form-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={1}
                className="w-full border rounded-md px-3 py-2 resize-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Logistic Partner</label>
                <input
                  id="warehouse-stock-transfer-replacement-fg-form-logistic-partner"
                  type="text"
                  value={logisticPartner}
                  onChange={(e) => setLogisticPartner(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                  placeholder="Enter logistic partner"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Tracking ID</label>
                <input
                  id="warehouse-stock-transfer-replacement-fg-form-tracking-id"
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                  placeholder="Enter tracking ID"
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Item Details</h2>
              </div>

              <div className="space-y-3">
                {items.map((item, i) => (
                  <div
                    key={i}
                    id={`warehouse-stock-transfer-replacement-fg-form-item-row-${item.itemCode ?? i}`}
                    className="p-4 border rounded-xl bg-gray-50 space-y-3"
                  >
                    <div className="font-medium">
                      {item.itemName} - {item.itemCode}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium">Quantity</label>
                        <input
                          id={`warehouse-stock-transfer-replacement-fg-form-item-row-${item.itemCode ?? i}-quantity`}
                          type="number"
                          min={1}
                          max={faultyDevices?.length || 0}
                          value={item.quantity}
                          disabled={manualItemMode}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            const maxDevices = faultyDevices?.length || 0;

                            setItems((prev) => {
                              const updated = [...prev];

                              //  prevent invalid values
                              if (value <= 0) return prev;

                              //  prevent quantity from exceeding total faulty devices count
                              if (value > maxDevices) {
                                showToast({
                                  type: ToastType.warning,
                                  text: `Only ${maxDevices} faulty device(s) available`,
                                });
                                return prev;
                              }

                              updated[i].quantity = value;
                              return updated;
                            });
                          }}
                          className="w-full h-10 border rounded-md px-3"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-medium">
                          Unit Price
                        </label>
                        <input
                          id={`warehouse-stock-transfer-replacement-fg-form-item-row-${item.itemCode ?? i}-unit-price`}
                          type="number"
                          min={0}
                          value={item.unitPrice}
                          disabled={item.isWacFetched ?? item.unitPrice > 0}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[i].unitPrice = Number(e.target.value);
                            setItems(updated);
                          }}
                          className="w-full h-10 border rounded-md px-3"
                          placeholder="Unit Price"
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-xs">
                      <p className="text-green-700 font-medium">
                        Available Units: {item.availableQty}
                      </p>

                      <button
                        id={`warehouse-stock-transfer-replacement-fg-form-item-row-${item.itemCode ?? i}-remove-btn`}
                        onClick={() => removeItem(i)}
                        className="text-red-600"
                      >
                        <Icon path={mdiTrashCanOutline} size={0.8} />
                      </button>
                    </div>
                  </div>
                ))}

                {items.length === 0 && (
                  <div className="text-center py-6 space-y-3">
                    <p className="text-sm text-gray-500">No items added</p>

                    <div className="max-w-md mx-auto">
                      <ItemSearchFieldV2
                        id="warehouse-stock-transfer-replacement-fg-form-manual-item-search"
                        disabled={false}
                        value={undefined}
                        subcategory={"Finished Goods"}
                        onSelect={async (item: any) => {
                          if (!item) return;

                          const wac = await fetchItemWac(item.item_code);
                          if (!wac.isFetched) {
                            showToast({
                              type: ToastType.warning,
                              text: "WAC not found. Enter unit price manually.",
                            });
                          }

                          setItems((prev) => {
                            const exists = prev.find(
                              (i) => i.itemCode === item.item_code,
                            );

                            if (exists) return prev;

                            const matchedDeviceCount = fetchedDevices.filter(
                              (d: any) => d.item_code === item.item_code,
                            ).length;
                            const initialQty =
                              matchedDeviceCount || faultyDevices?.length || 1;

                            return [
                              ...prev,
                              {
                                itemCode: item.item_code,
                                itemName: item.name,
                                quantity: initialQty,
                                unitPrice: wac.value,
                                isWacFetched: wac.isFetched,
                                availableQty: fetchAvailableQty(item.item_code),
                                itemUnit: item.unit.formal_name || "",
                                itemHsn: item.tax_detail.hsn_sac || "",
                                subcategory: item.subcategory || "",
                              },
                            ];
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {error}
              </p>
            )}
          </div>

          <div className="border-t px-6 py-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div className="font-semibold">
              Total Value:{" "}
              <span className="text-blue-600">
                ?{totalValue.toLocaleString()}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                id="warehouse-stock-transfer-replacement-fg-form-cancel-btn"
                onClick={() => history.goBack()}
                disabled={loading}
                className="border px-6 py-2 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                id="warehouse-stock-transfer-replacement-fg-form-submit-btn"
                onClick={handleCreate}
                disabled={loading}
                className={`bg-blue-600 text-white px-6 py-2 rounded-lg transition-all ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-700"
                }`}
              >
                {loading ? "Creating..." : "Create Replacement ST"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <LoadingWidget />}
    </div>
  );
}
